import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Heading,
  HiddenVisually,
  Stack,
} from 'braid-design-system';
import { memo } from 'react';

import { useHotjarEvent } from 'src/hooks/useHotjarEvent';
import {
  useIsHomepageGenAIChatAltModelEnabled,
  useIsHomepageGenAIChatEnabled,
} from 'src/store/featureFlags/hooks';
import { useSavedRecentSearchesFeatureFlag } from 'src/store/featureFlags/useSavedRecentSearchesFeatureFlag';

import translations from './.vocab';
import { CareerFeedAIAssistant } from './CareerFeedAIAssistant/CareerFeedAIAssistant';
import { CareerFeedAIAssistantProvider } from './CareerFeedAIAssistant/hooks/useCareerFeedAIAssistant';
import { MoreRecommendations } from './DashboardRecommendations/MoreRecommendations';
import {
  RECOMMENDED_JOBS_SPACE,
  TopRecommendations,
} from './DashboardRecommendations/TopRecommendations';
import SavedSearches from './DashboardSaveSearches/SavedSearches';
import SavedJobs from './DashboardSavedJobs/SavedJobs';
import { MyActivityTiles } from './MyActivityTiles/MyActivityTiles';
import { JobDetailsViewDrawer } from './SharedComponent/JobDetailsViewDrawer/JobDetailsViewDrawer';
import { SignedInDashboardContextProvider } from './SignedInDashboardContextProvider';

const SignedInDashboard = () => {
  const { t } = useTranslations(translations);
  const isHomepageGenAIChatEnabled = useIsHomepageGenAIChatEnabled();
  const isHomepageGenAIChatAltModelEnabled =
    useIsHomepageGenAIChatAltModelEnabled();

  useHotjarEvent('homepage_layout_refresh_experiment');
  const isSavedRecentSearches = useSavedRecentSearchesFeatureFlag();

  return (
    <CareerFeedAIAssistantProvider
      useAltModel={isHomepageGenAIChatAltModelEnabled}
    >
      <SignedInDashboardContextProvider>
        <HiddenVisually>
          <h1>{t('Dashboard')}</h1>
        </HiddenVisually>

        <Columns space="xlarge">
          <Column>
            <Stack space="medium">
              {!isSavedRecentSearches && <MyActivityTiles />}
              {isHomepageGenAIChatEnabled && (
                <Box display={{ mobile: 'block', desktop: 'none' }}>
                  <CareerFeedAIAssistant />
                </Box>
              )}
              <Stack space="xsmall">
                <TopRecommendations />
                <MoreRecommendations />
              </Stack>
            </Stack>
          </Column>
          <Column width="2/5" hideBelow="desktop">
            <Stack space="large">
              {isHomepageGenAIChatEnabled && (
                <Stack space={RECOMMENDED_JOBS_SPACE}>
                  <Box userSelect="none">
                    <Heading level="3">&nbsp;</Heading>
                  </Box>
                  <CareerFeedAIAssistant />
                </Stack>
              )}
              {!isSavedRecentSearches && <SavedSearches />}
              <SavedJobs />
            </Stack>
          </Column>
        </Columns>
        <JobDetailsViewDrawer />
      </SignedInDashboardContextProvider>
    </CareerFeedAIAssistantProvider>
  );
};

export default memo(SignedInDashboard);
